import { useEffect, useRef, useState } from "react";
import { isActiveTableSessionStatus, useTableSession } from '../../../context/TableSessionContext';
import { useTranslation } from 'react-i18next';
import "./ContextBar.css";

/* ============================================================
   Iconos
   ============================================================ */
const ICONS = {
    grid: (
        <>
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
        </>
    ),
    table: (
        <>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M12 3v18" />
            <path d="M3 9h18" />
            <path d="M3 15h18" />
        </>
    ),
    mappin: (
        <>
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
            <circle cx="12" cy="10" r="3" />
        </>
    ),
    clock: (
        <>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
        </>
    ),
    user: (
        <>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </>
    ),
    crown: (
        <>
            <path d="M5 21h14" />
            <path d="M11.56 3.27a.5.5 0 0 1 .88 0l3 5.6a1 1 0 0 0 1.5.3l4.28-3.67a.5.5 0 0 1 .8.52L19.2 16.3a1 1 0 0 1-.96.73H5.76a1 1 0 0 1-.96-.73L2 6.02a.5.5 0 0 1 .8-.52l4.27 3.67a1 1 0 0 0 1.5-.3z" />
        </>
    ),
};

function Icon({ name, strokeWidth = 1.75 }) {
    const path = ICONS[name];
    if (!path) return null;
    return (
        <svg
            className="sp-ico"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {path}
        </svg>
    );
}

/* ============================================================
   TruncChip — chip reutilizable con prefijo + valor.
   Si el valor no cabe, trunca con "…" y al tocarlo abre un
   popover con el texto completo. Sirve para Mesa y para Zona.
   ============================================================ */
function TruncChip({ icon, prefix, label }) {
    const labelRef = useRef(null);
    const [truncated, setTruncated] = useState(false);
    const [open, setOpen] = useState(false);

    // Mide si el valor desborda (re-mide al redimensionar y al cargar la fuente)
    useEffect(() => {
        const measure = () => {
            const el = labelRef.current;
            if (el) setTruncated(el.scrollWidth > el.clientWidth + 1);
        };
        measure();
        window.addEventListener("resize", measure);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
        return () => window.removeEventListener("resize", measure);
    }, [label, prefix]);

    // Cerrar al tocar fuera o con Escape
    useEffect(() => {
        if (!open) return;
        const close = () => setOpen(false);
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("click", close);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("click", close);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    // Si deja de estar truncado, cerramos
    useEffect(() => {
        if (!truncated) setOpen(false);
    }, [truncated]);

    const cls =
        "sp-zone" +
        (truncated ? " is-truncated" : "") +
        (open ? " is-open" : "");

    const full = prefix ? `${prefix}: ${label}` : label;

    return (
        <span className={cls}>
            <button
                className="sp-zone__btn"
                type="button"
                aria-haspopup={truncated}
                aria-expanded={open}
                onClick={(e) => {
                    e.stopPropagation();
                    if (truncated) setOpen((o) => !o);
                }}
            >
                <Icon name={icon} />
                {prefix && <span className="sp-zone__prefix">{prefix}:</span>}
                <span className="sp-zone__label" ref={labelRef}>
                    {label}
                </span>
            </button>
            <span className="sp-zone__pop" role="tooltip">
                {full}
            </span>
        </span>
    );
}

/* ============================================================
   ContextBar — componente principal
   Izquierda: Mesa · Derecha: Zona + Sesión activa
   ============================================================ */
export default function ContextBar() {
    const { t } = useTranslation();
    const { session: tableSession, status: tableSessionStatus } = useTableSession();

    const hasActiveTableSession = isActiveTableSessionStatus(tableSessionStatus);
    const tableSessionLabel = tableSession?.table?.name || tableSession?.table?.number || "";
    const tableEnvironmentLabel = tableSession?.table?.environment || "";

    if (!hasActiveTableSession) return null;

    return (
        <nav
            className="sp-context-bar"
            aria-label={t("contextBar.aria", { defaultValue: "Contexto de la mesa" })}
        >
            {/* IZQUIERDA — Mesa (trunca si es larga) */}
            {tableSessionLabel.length > 0 && (
                <div className="sp-context-bar__lead">
                    <TruncChip
                        icon="table"
                        prefix={t("contextBar.table", { defaultValue: "Mesa" })}
                        label={tableSessionLabel}
                    />
                </div>
            )}

            {/* DERECHA — Zona (trunca si es larga) + Sesión activa */}
            <div className="sp-context-bar__status">
                {tableEnvironmentLabel.length > 0 && (
                    <TruncChip
                        icon="mappin"
                        prefix={t("contextBar.zone", { defaultValue: "Zona" })}
                        label={tableEnvironmentLabel}
                    />
                )}

                <span className="sp-session">
                    <span className="sp-session__dot" aria-hidden="true" />
                    {t("contextBar.active", { defaultValue: "Sesión activa" })}
                </span>
            </div>
        </nav>
    );
}

export { ContextBar, TruncChip };